// Checkbox: a native checkbox drawn as a recessed well that takes a mark. The tick draws itself
// when it lands; a mixed state is a bar. Gold by default, green where checked means done.
// CheckMark is the face on its own, shared with CheckRow and the radio dot.
// docs/operations/design-standard.md, "Controls".
//
// The native input stays in the tree, clipped (sr-only), so forms, labels, Space, and the shared
// table's "Enter opens the row, but not from an input" rule all keep working. sr-only also beats
// index.css's global checkbox rule, whose selector is held to (0,0,1) for exactly this.

import React, { useEffect, useRef, useState } from 'react';
import { CONTROL_MOTION, PEER_FOCUS, TOUCH } from './motion';

export type CheckTone = 'primary' | 'good';
export type MarkTone = CheckTone | 'warn';
export type CheckState = boolean | 'mixed';
export type MarkSize = 'sm' | 'md' | 'lg';

// The edge is on-surface-disabled (#7A7A7A) because it holds 3:1 against the panel, raised and
// track steps: an empty box is identified by its edge alone.
const EDGE = {
  thin: 'shadow-[inset_0_0_0_1.5px_var(--on-surface-disabled),inset_0_1px_2px_rgba(0,0,0,0.35)] group-hover/mark:shadow-[inset_0_0_0_1.5px_var(--on-surface-variant),inset_0_1px_2px_rgba(0,0,0,0.35)]',
  thick: 'shadow-[inset_0_0_0_2px_var(--on-surface-disabled),inset_0_1px_2px_rgba(0,0,0,0.35)] group-hover/mark:shadow-[inset_0_0_0_2px_var(--on-surface-variant),inset_0_1px_2px_rgba(0,0,0,0.35)]',
};

const LIT: Record<MarkTone, string> = {
  primary: 'bg-primary text-on-primary np-state-on-primary',
  good: 'bg-green-500 text-surface np-state-on-primary',
  warn: 'bg-amber-500/15 text-amber-300 shadow-[inset_0_0_0_1.5px_rgb(245_158_11/0.5)]',
};

/** The hover layer follows the whole label, not only the face. */
const HOVER_LAYER = 'np-state group-hover/mark:[--np-layer:var(--np-state-hover,var(--np-hover-color))]';

/** The face of a check box or a radio dot, by state. The host carries `group/mark`. */
export const markFace = (lit: MarkTone | null, disabled: boolean, size: MarkSize = 'md'): string => {
  if (disabled) {
    return lit ? 'bg-surface-container-highest text-on-surface-disabled' : 'bg-surface-container text-transparent shadow-[inset_0_0_0_1.5px_var(--outline-variant)]';
  }
  return `${HOVER_LAYER} ${lit ? LIT[lit] : `bg-surface-container-high text-transparent ${size === 'lg' ? EDGE.thick : EDGE.thin}`}`;
};

export const MARK_BOX: Record<MarkSize, string> = { sm: 'h-4 w-4', md: 'h-[18px] w-[18px]', lg: 'h-6 w-6' };
const GLYPH: Record<MarkSize, string> = { sm: 'h-3 w-3', md: 'h-3.5 w-3.5', lg: 'h-4 w-4' };

export interface CheckMarkProps {
  state: CheckState;
  tone?: CheckTone;
  /** The fill for the mixed state; CheckRow uses `warn` for an item set aside. */
  mixedTone?: MarkTone;
  size?: MarkSize;
  disabled?: boolean;
  className?: string;
}

/** The drawn box: a well with an edge until it is on, then the tone's fill and a tick that draws itself. */
export const CheckMark = React.forwardRef<HTMLSpanElement, CheckMarkProps>(
  ({ state, tone = 'primary', mixedTone, size = 'md', disabled = false, className = '' }, ref) => {
    const lit: MarkTone | null = state === true ? tone : state === 'mixed' ? mixedTone || tone : null;
    return (
      <span
        ref={ref}
        aria-hidden="true"
        className={`relative inline-flex flex-none items-center justify-center rounded-control motion-safe:group-active/mark:scale-90 ${MARK_BOX[size]} ${markFace(lit, disabled, size)} ${CONTROL_MOTION} ${className}`}
      >
        <svg viewBox="0 0 24 24" fill="none" focusable="false" className={GLYPH[size]}>
          {/* The one stroke: a tick that draws itself. Lands on the standard, leaves on the exit. */}
          <path
            d="M5 12.5l4.5 4.5L19 7.5"
            stroke="currentColor"
            strokeWidth={3}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray={24}
            style={{ strokeDashoffset: state === true ? 0 : 24 }}
            className={`transition-[stroke-dashoffset] motion-reduce:transition-none ${
              state === true ? 'duration-standard ease-emphasized' : 'duration-exit ease-exit'
            }`}
          />
          <rect
            x="5.5"
            y="10.75"
            width="13"
            height="2.5"
            rx="1.25"
            fill="currentColor"
            className={`origin-center transition-transform duration-standard ease-emphasized motion-reduce:transition-none ${
              state === 'mixed' ? 'scale-x-100' : 'scale-x-0'
            }`}
          />
        </svg>
      </span>
    );
  },
);
CheckMark.displayName = 'CheckMark';

let seq = 0;

/** Props for the wrapping label (not className, which the control owns). */
export type LabelProps = Omit<React.LabelHTMLAttributes<HTMLLabelElement>, 'className' | 'htmlFor'>;

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  /** The words beside the box. Omit for a bare box, and name it with aria-label. */
  label?: React.ReactNode;
  /** A quieter line under the label; spoken as the description, not the name. */
  description?: React.ReactNode;
  /** `start` puts the words first: a settings row reads its question, then the box. */
  labelSide?: 'end' | 'start';
  /** Take the row's width, the words filling the space between. */
  fill?: boolean;
  /** `good` where checked means done. */
  tone?: CheckTone;
  /** Some but not all: the select-all box over a partial selection. */
  indeterminate?: boolean;
  /** `sm` (16px) in table cells, `md` (18px) everywhere else. */
  size?: 'sm' | 'md';
  /** On the wrapping label: a `title` hint, or an `onClick` that stops a click reaching a clickable row. Extra props on the control land on the hidden input, where neither works. */
  labelProps?: LabelProps;
}

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      description,
      labelSide = 'end',
      fill = false,
      tone = 'primary',
      indeterminate = false,
      size = 'md',
      className = '',
      labelProps,
      checked,
      defaultChecked,
      onChange,
      disabled = false,
      'aria-describedby': describedBy,
      ...rest
    },
    ref,
  ) => {
    const [ids] = useState(() => {
      seq += 1;
      return { label: `ui-check-${seq}-label`, note: `ui-check-${seq}-note` };
    });
    const input = useRef<HTMLInputElement | null>(null);
    const [own, setOwn] = useState(!!defaultChecked);
    const on = checked !== undefined ? !!checked : own;

    // Every render, not on change: a click clears the DOM flag while the prop may stay true.
    useEffect(() => {
      if (input.current) input.current.indeterminate = indeterminate;
    });

    const setRef = (el: HTMLInputElement | null) => {
      input.current = el;
      if (typeof ref === 'function') ref(el);
      else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = el;
    };

    const named = !!(rest['aria-label'] || rest['aria-labelledby']);
    const words = label !== undefined || description !== undefined;
    const layout = fill ? 'flex w-full' : 'inline-flex max-w-full';
    const text = size === 'sm' ? 'text-[13px] leading-[18px]' : 'text-sm leading-5';

    return (
      <label
        {...labelProps}
        className={`group/mark relative min-w-0 select-none gap-2 ${layout} ${description ? 'items-start' : 'items-center'} ${
          labelSide === 'start' ? 'flex-row-reverse' : ''
        } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${className}`}
      >
        <input
          ref={setRef}
          type="checkbox"
          className="peer sr-only"
          checked={checked}
          defaultChecked={defaultChecked}
          disabled={disabled}
          aria-labelledby={description !== undefined && label !== undefined && !named ? ids.label : undefined}
          aria-describedby={[describedBy, description !== undefined ? ids.note : undefined].filter(Boolean).join(' ') || undefined}
          onChange={(e) => {
            if (checked === undefined) setOwn(e.target.checked);
            onChange?.(e);
          }}
          {...rest}
        />
        <CheckMark
          state={indeterminate ? 'mixed' : on}
          tone={tone}
          size={size}
          disabled={disabled}
          className={`${PEER_FOCUS} ${TOUCH} ${description ? 'mt-px' : ''}`}
        />
        {words && (
          <span className={`min-w-0 ${fill ? 'flex-1' : ''}`}>
            {label !== undefined && (
              <span id={ids.label} className={`block ${text} ${disabled ? 'text-on-surface-disabled' : 'text-on-surface'}`}>
                {label}
              </span>
            )}
            {description !== undefined && (
              <span id={ids.note} className={`mt-0.5 block text-xs leading-4 ${disabled ? 'text-on-surface-disabled' : 'text-on-surface-variant'}`}>
                {description}
              </span>
            )}
          </span>
        )}
      </label>
    );
  },
);
Checkbox.displayName = 'Checkbox';

export default Checkbox;
